import React, { useCallback, useEffect, useState } from 'react';
import { Drawer, Space, Typography, Button, Upload, Spin, Input } from 'antd';
import type { RcFile } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ToolService from '../../services/ToolService';
import DocumentService from '../../services/DocumentService';
import { openNotification } from '../../utils';

interface ToolPreparationDrawerProps {
  toolId: number | null;
}

export const TOOL_PREPARATION_UPDATED_EVENT = 'tool-preparation-updated';

const normalizePreparationText = (value: string): string | null => {
  if (!value) {
    return null;
  }
  const parsedHtml = new DOMParser().parseFromString(value, 'text/html');
  const plainText = (parsedHtml.body.textContent ?? '')
    .replace(/\u00a0/g, ' ')
    .trim();
  return plainText ? value : null;
};

const ToolPreparationDrawer: React.FC<ToolPreparationDrawerProps> = ({ toolId }) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toolName, setToolName] = useState<string>('');
  const [editorValue, setEditorValue] = useState<string>('');
  const [titleValue, setTitleValue] = useState<string>('');
  const [payload, setPayload] = useState<any | null>(null);
  const [selectedFile, setSelectedFile] = useState<RcFile | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [documentId, setDocumentId] = useState<number | null>(null);
  const [existingDocument, setExistingDocument] = useState<any | null>(null);

  const resetState = useCallback(() => {
    setVisible(false);
    setLoading(false);
    setSaving(false);
    setToolName('');
    setEditorValue('');
    setTitleValue('');
    setPayload(null);
    setSelectedFile(null);
    setFileList([]);
    setDocumentId(null);
    setExistingDocument(null);
  }, []);

  const buildUpdatePayload = useCallback((tool: any) => {
    if (!tool) {
      return null;
    }
    return {
      id: tool.id,
      name: tool.name,
      description: tool.description,
      preparationText: tool.preparationText ?? '',
      preparationTitle: tool.preparationTitle ?? '',
      preparationDocumentId: tool.preparationDocumentId ?? tool.preparationDocument?.id ?? null,
    };
  }, []);

  const fetchToolDetails = useCallback(
    async (id: number) => {
      setLoading(true);
      setSelectedFile(null);
      setFileList([]);
      try {
        const response = await ToolService.getTool(id);
        const detail = response.data;
        const preparedPayload = buildUpdatePayload(detail);
        if (!preparedPayload) {
          throw new Error('Bileşen bilgisi alınamadı');
        }
        setPayload(preparedPayload);
        setEditorValue(preparedPayload.preparationText ?? '');
        setTitleValue(preparedPayload.preparationTitle ?? '');
        setDocumentId(preparedPayload.preparationDocumentId ?? null);
        setToolName(detail?.name ?? '');

        if (preparedPayload.preparationDocumentId) {
          try {
            const documentResponse: any = await DocumentService.getDocument(preparedPayload.preparationDocumentId);
            setExistingDocument(documentResponse?.data || null);
          } catch (docError) {
            console.error('Error fetching preparation document:', docError);
            setExistingDocument(null);
          }
        } else {
          setExistingDocument(null);
        }
      } catch (error) {
        console.error('Error fetching tool preparation info:', error);
        openNotification('error', 'Hata', 'Ön hazırlık bilgileri alınamadı.');
        resetState();
      } finally {
        setLoading(false);
      }
    },
    [buildUpdatePayload, resetState]
  );

  useEffect(() => {
    if (!toolId) {
      resetState();
      return;
    }
    setVisible(true);
    fetchToolDetails(toolId);
  }, [toolId, fetchToolDetails, resetState]);

  const handleBeforeUpload = (file: RcFile) => {
    const isPdf =
      file.type === 'application/pdf' ||
      file.name?.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      openNotification('error', 'Hata', 'Lütfen PDF formatında bir dosya seçiniz.');
      return Upload.LIST_IGNORE;
    }
    const uploadItem: UploadFile = {
      uid: `${Date.now()}`,
      name: file.name,
      status: 'done',
    };
    setSelectedFile(file);
    setFileList([uploadItem]);
    setDocumentId(null);
    setExistingDocument(null);
    return false;
  };

  const handleRemoveSelectedFile = () => {
    setSelectedFile(null);
    setFileList([]);
    return true;
  };

  const handleRemoveExistingDocument = () => {
    setDocumentId(null);
    setExistingDocument(null);
    setSelectedFile(null);
    setFileList([]);
  };

  const handleClose = () => {
    setVisible(false);
  };

  const handleSave = async () => {
    if (!payload || !payload.id) {
      return;
    }
    setSaving(true);
    try {
      let preparedDocumentId = documentId;
      if (selectedFile) {
        const uploadResponse: any = await DocumentService.postDocument(selectedFile as any, selectedFile.name);
        preparedDocumentId =
          uploadResponse?.data?.id ??
          uploadResponse?.data?.data?.id ??
          null;
        if (!preparedDocumentId) {
          throw new Error('Belge yüklenemedi');
        }
      }

      const updatePayload = {
        name: payload.name,
        description: payload.description,
        preparationText: normalizePreparationText(editorValue),
        preparationTitle: titleValue.trim(),
        preparationDocumentId: preparedDocumentId,
      };

      await ToolService.updateTool(payload.id, updatePayload);
      openNotification('success', 'Başarılı', 'Ön hazırlık bilgileri güncellendi.');
      window.dispatchEvent(new CustomEvent(TOOL_PREPARATION_UPDATED_EVENT, { detail: { toolId: payload.id } }));
      setVisible(false);
    } catch (error) {
      console.error('Error updating preparation info:', error);
      openNotification('error', 'Hata', 'Ön hazırlık bilgileri güncellenemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={visible && Boolean(toolId)}
      onClose={handleClose}
      destroyOnClose
      width={720}
      title={
        toolName
          ? `${toolName} Ön Hazırlık Metni`
          : 'Ön Hazırlık Metni'
      }
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 48 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Typography.Text strong>Başlık</Typography.Text>
            <Input
              value={titleValue}
              onChange={e => setTitleValue(e.target.value)}
              maxLength={255}
              placeholder="Başlık giriniz"
              style={{ marginTop: 12 }}
            />
          </div>
          <div>
            <Typography.Text strong>Ön Hazırlık Metni</Typography.Text>
            <div style={{ marginTop: 12 }}>
              <ReactQuill
                theme="snow"
                value={editorValue}
                onChange={setEditorValue}
                style={{ minHeight: 200 }}
              />
            </div>
          </div>
          <div>
            <Typography.Text strong>Ön Hazırlık PDF</Typography.Text>
            <Space direction="vertical" style={{ width: '100%', marginTop: 12 }}>
              {existingDocument ? (
                <Space direction="vertical" size={4}>
                  <Typography.Text>
                    {existingDocument?.code ?? 'Mevcut dosya'}
                  </Typography.Text>
                  {existingDocument?.preSignUrl ? (
                    <Button
                      type="link"
                      href={existingDocument.preSignUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Dosyayı İndir
                    </Button>
                  ) : null}
                  <Button type="link" danger onClick={handleRemoveExistingDocument}>
                    Mevcut Dosyayı Kaldır
                  </Button>
                </Space>
              ) : null}
              <Upload
                accept=".pdf"
                beforeUpload={handleBeforeUpload}
                onRemove={handleRemoveSelectedFile}
                fileList={fileList}
                maxCount={1}
              >
                <Button>PDF Seç</Button>
              </Upload>
              {selectedFile ? (
                <Typography.Text type="secondary">
                  Seçilen dosya: {selectedFile.name}
                </Typography.Text>
              ) : null}
              {!existingDocument && !selectedFile ? (
                <Typography.Text type="secondary">
                  PDF yüklemediğinizde bileşene ait doküman kaydedilmez.
                </Typography.Text>
              ) : null}
            </Space>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={handleClose}>İptal</Button>
            <Button
              type="primary"
              loading={saving}
              onClick={handleSave}
              disabled={loading}
              className="d-button dark-button width-auto"
            >
              Kaydet
            </Button>
          </div>
        </Space>
      )}
    </Drawer>
  );
};

export default ToolPreparationDrawer;
